import { sortDateDesc } from '@strata/helper-functions';
import { BaseNotification } from '@strata/react-notifications';

import { SocketTypes, wsSend } from 'services/socketHelpers';
import { type AppThunk } from 'store';
import {
    addNotifications,
    removeFromList,
    setIncomingNotification,
    setNotificationStatuses,
} from 'store/notification/notificationSlice';

export interface NewNotification {
    notification_id: string;
    notification_type: (typeof BaseNotification.Types)[keyof typeof BaseNotification.Types];
    linking_id: string;
    payer_id: string;
    segment_id: string;
    notification_time: string;
    interaction_status: (typeof BaseNotification.Statuses)[keyof typeof BaseNotification.Statuses];
    additional_info: object;
}

export const receiveNewNotifications =
    (notifications: NewNotification[]): AppThunk =>
    (dispatch, getState) => {
        const { notification } = getState();
        const existingNotificationIds = notification.notifications.map((obj) => obj.id);
        const newNotifications = Array.from(notifications)
            .filter(
                (newNotification) =>
                    !existingNotificationIds.includes(newNotification.notification_id),
            )
            .map((newNotification) => ({
                id: newNotification.notification_id,
                type: newNotification.notification_type,
                linkingId: newNotification.linking_id,
                payerId: newNotification.payer_id,
                segmentId: newNotification.segment_id,
                timestamp: newNotification.notification_time,
                status: newNotification.interaction_status,
                additionalData: newNotification.additional_info,
            }))
            .sort((a, b) => sortDateDesc(a.timestamp, b.timestamp));

        if (notification.notificationsLoaded && newNotifications.length > 0) {
            newNotifications.forEach((incomingNotification) =>
                dispatch(setIncomingNotification({ incomingNotification })),
            );
        }

        dispatch(addNotifications({ newNotifications }));
    };

export const updateNotificationStatuses =
    (
        status: (typeof BaseNotification.Statuses)[keyof typeof BaseNotification.Statuses],
        notificationIds: string[],
    ): AppThunk =>
    (dispatch) => {
        dispatch(
            wsSend({
                socketType: SocketTypes.NOTIFICATION,
                messageType: status,
                message: notificationIds,
            }),
        );

        if (status === BaseNotification.Statuses.DELETED) {
            dispatch(removeFromList({ notificationIds }));
        } else {
            dispatch(setNotificationStatuses({ notificationIds, status }));
        }
    };

export const updateNotificationStatus =
    (
        status: (typeof BaseNotification.Statuses)[keyof typeof BaseNotification.Statuses],
        notificationId: string | undefined,
    ): AppThunk =>
    (dispatch) => {
        if (notificationId) {
            dispatch(updateNotificationStatuses(status, [notificationId]));
        }
    };

export const clearIncomingNotification = (): AppThunk => (dispatch) => {
    dispatch(setIncomingNotification({ incomingNotification: null }));
};
